// Stall detection + retry policy for one chat turn. Pure — no React, no DOM —
// so the dashboard's vitest runner (no jsdom) can pin every rule below.
//
// WHY a second watchdog. The transports already abort when no BYTES arrive for
// 30s; that catches a dead socket. It cannot catch a wedged model: the backend
// injects a `: ping` comment every 5s of silence (`with_heartbeat`), so bytes
// keep flowing while the generation hangs, and the turn only ends when the
// server's own LLM timeout fires (NETWATCH_LLM_TIMEOUT_SECONDS, 300s). Users
// saw a 10-second question sit on "Thinking…" for five minutes. Heartbeat
// comments never become SSE frames (`parseSSE` drops them), so a watchdog fed
// by FRAMES — tokens, tool calls, node completions, anything the reducer sees —
// measures progress, not liveness. That is what `createStallWatchdog` does;
// `planRetry` decides what happens when it trips.

import type { ChatMessage, TurnFailure } from './types'
import type { StreamBody } from './transport'

/** No frame for this long and the turn is declared stalled. Comfortably above
 *  the longest legitimately silent step (the non-streaming quality audit is
 *  capped at 90s server-side) so it trips on a hang, not on a slow model. */
export const DEFAULT_STALL_TIMEOUT_MS = 90_000

/** Automatic re-sends of a stalled turn before it is marked failed. */
export const DEFAULT_STALL_RETRIES = 1

/** Pause before re-sending a stalled turn. The server only notices our abort
 *  on its next heartbeat tick (≤5s) and then tears the run down; re-sending
 *  sooner just meets the per-thread single-flight guard. */
export const STALL_RETRY_DELAY_MS = 2_000

/** Waits between re-sends while the server still reports the previous attempt
 *  as in flight (the teardown outran our delay). Exhausting the ladder fails
 *  the turn with the server's own message. */
export const BUSY_RETRY_DELAYS_MS: readonly number[] = [3_000, 5_000, 8_000]

/** `code` the backend stamps on the "still working on the previous message"
 *  error frame (HTTP 409 semantics) so the client can tell it from a real
 *  failure without matching prose. */
export const BUSY_CODE = 409

/** Thrown by the reducer when the server rejects a send with the busy frame. */
export class TurnBusyError extends Error {
  constructor(detail: string) {
    super(detail)
    this.name = 'TurnBusyError'
  }
}

export interface StallWatchdog {
  /** A frame arrived — restart the countdown. */
  touch: () => void
  /** The turn is over — cancel the countdown; `onStall` will not fire. */
  stop: () => void
  /** True once the countdown elapsed and `onStall` fired. */
  readonly tripped: boolean
}

/**
 * Countdown that fires `onStall` once when `timeoutMs` passes without a
 * `touch()`. Fires at most once; `stop()` cancels it. A non-positive or
 * non-finite timeout disables it (never fires).
 */
export function createStallWatchdog(timeoutMs: number, onStall: () => void): StallWatchdog {
  const enabled = Number.isFinite(timeoutMs) && timeoutMs > 0
  let timer: ReturnType<typeof setTimeout> | null = null
  let tripped = false
  let stopped = false
  const arm = () => {
    if (!enabled || stopped || tripped) return
    if (timer !== null) clearTimeout(timer)
    timer = setTimeout(() => {
      timer = null
      tripped = true
      onStall()
    }, timeoutMs)
  }
  arm()
  return {
    touch: arm,
    stop: () => {
      stopped = true
      if (timer !== null) clearTimeout(timer)
      timer = null
    },
    get tripped() {
      return tripped
    },
  }
}

/** How one attempt at a turn ended, as far as the retry policy cares. */
export type AttemptOutcome =
  | { kind: 'stall' }
  | { kind: 'busy'; detail: string }
  | { kind: 'error'; detail: string }

export interface RetryPolicy {
  stallRetries: number
  stallRetryDelayMs: number
  busyRetryDelaysMs: readonly number[]
}

export interface RetryState {
  /** Stall re-sends already made this turn. */
  stallRetries: number
  /** Busy waits already made since the last stall re-send. */
  busyRetries: number
}

export const INITIAL_RETRY_STATE: RetryState = { stallRetries: 0, busyRetries: 0 }

export type RetryPlan =
  | { retry: true; delayMs: number; state: RetryState }
  | { retry: false; failure: TurnFailure }

/** User-facing detail for a turn given up on after `attempts` stalls. */
export function stallFailureDetail(timeoutMs: number, attempts: number): string {
  const secs = Math.round(timeoutMs / 1000)
  const tries = attempts > 1 ? ` on ${attempts} attempts` : ''
  return `No progress for ${secs}s${tries}. The answer may still be slow to produce right now — try again in a moment.`
}

/**
 * Decide what a turn does after an attempt ends badly.
 *
 * - `stall`: re-send while stall retries remain, then fail as `stall`.
 * - `busy`: the server says the previous attempt is still winding down. Only
 *   auto-wait when WE caused the collision — this send is a re-send of an
 *   aborted attempt (`ctx.attempts > 1`: an automatic stall re-send, or the
 *   user pressing Retry right after a stall failure, before the server has
 *   torn the aborted run down). A FIRST send meeting a busy thread is the
 *   double-tab case and the server's message stands. The wait ladder resets
 *   on every stall re-send.
 * - `error`: fail as `error` with the message as-is.
 */
export function planRetry(
  outcome: AttemptOutcome,
  state: RetryState,
  policy: RetryPolicy,
  ctx: { timeoutMs: number; attempts: number },
): RetryPlan {
  switch (outcome.kind) {
    case 'stall':
      if (state.stallRetries < policy.stallRetries) {
        return {
          retry: true,
          delayMs: policy.stallRetryDelayMs,
          state: { stallRetries: state.stallRetries + 1, busyRetries: 0 },
        }
      }
      return {
        retry: false,
        failure: { reason: 'stall', detail: stallFailureDetail(ctx.timeoutMs, ctx.attempts) },
      }
    case 'busy':
      if (ctx.attempts > 1 && state.busyRetries < policy.busyRetryDelaysMs.length) {
        return {
          retry: true,
          delayMs: policy.busyRetryDelaysMs[state.busyRetries],
          state: { ...state, busyRetries: state.busyRetries + 1 },
        }
      }
      return { retry: false, failure: { reason: 'error', detail: outcome.detail } }
    case 'error':
      return { retry: false, failure: { reason: 'error', detail: outcome.detail } }
  }
}

/**
 * Rebuild the request for a manual retry when the original body is gone (the
 * page was reloaded and the failed bubble came back from localStorage): the
 * nearest user message before the failed assistant bubble, on the current
 * thread. Null when the transcript has no such message.
 */
export function retryBodyFor(
  messages: readonly ChatMessage[],
  failedId: number,
  threadId: string | null,
): StreamBody | null {
  const at = messages.findIndex((m) => m.role === 'assistant' && m.id === failedId)
  if (at < 0) return null
  for (let i = at - 1; i >= 0; i--) {
    const m = messages[i]
    if (m.role === 'user') {
      return { message: m.content, thread_id: threadId ?? undefined }
    }
  }
  return null
}

/** Sleep that resolves early (never rejects) when `signal` aborts. */
export function abortableDelay(ms: number, signal: AbortSignal): Promise<void> {
  return new Promise((resolve) => {
    if (signal.aborted) {
      resolve()
      return
    }
    const done = () => {
      clearTimeout(timer)
      signal.removeEventListener('abort', done)
      resolve()
    }
    const timer = setTimeout(done, ms)
    signal.addEventListener('abort', done)
  })
}
